import { useRef, useState } from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import { useDateLocale } from '../../../../../hooks'
import { isEmptyString } from '../../../../../utils/common'
import { getGooglePlacesAutocomplete } from '../../../Google.api'

import {
  getGoogleMapPlacesApiData,
  parseAutocompleteRpcResponse,
  toGoogleMapPlacesPreparedItems,
} from './useGoogleMapApiPlacesAutocomplete.utils'

/**
 * Origin https://github.com/visgl/react-google-maps/blob/main/examples/autocomplete/src/components/autocomplete-custom.tsx#L38
 *
 * A reusable hook that retrieves autocomplete suggestions from the Google Places API.
 * The data is loaded from the new Autocomplete Data API.
 * (https://developers.google.com/maps/documentation/javascript/place-autocomplete-data)
 *
 * @param inputString The input string for which to fetch autocomplete suggestions.
 * @param requestOptions Additional options for the autocomplete request
 *   (See {@link https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest}).
 *
 * @returns An object containing the autocomplete suggestions, the current loading-status,
 *   and a function to reset the session.
 */
export function useGoogleMapApiPlacesAutocomplete({
  parseResponse = parseAutocompleteRpcResponse,
  requestOptions = EMPTY_OBJECT,
  toPreparedItems = toGoogleMapPlacesPreparedItems,
} = EMPTY_OBJECT) {
  const placesLib = useMapsLibrary('places')

  const { code: locale } = useDateLocale()

  const sessionTokenRef = useRef(null)

  const [suggestions, setSuggestions] = useState(EMPTY_LIST)

  const [isLoading, setIsLoading] = useState(false)

  function clearSuggestions() {
    setSuggestions(EMPTY_LIST)
  }

  function fetchPredictions(searchValue) {
    if (placesLib) {
      const { AutocompleteSessionToken } = placesLib

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new AutocompleteSessionToken()
      }

      const request = {
        ...requestOptions,
        input: searchValue,
        languageCode: locale,
        sessionToken: sessionTokenRef.current?.token,
      }

      if (isEmptyString(searchValue.trim()) && suggestions.length > 0) {
        clearSuggestions()
      } else if (searchValue) {
        setIsLoading(true)

        getGooglePlacesAutocomplete(getGoogleMapPlacesApiData(request)).then(
          response => {
            setSuggestions(toPreparedItems(parseResponse(response.data)))
            setIsLoading(false)
          }
        )
      }
    }
  }

  function handleResetSession() {
    sessionTokenRef.current = null
    clearSuggestions()
  }

  return {
    clearSuggestions,
    fetchPredictions,
    handleResetSession,
    isInitiated: !!placesLib,
    isLoading,
    suggestions,
  }
}
